import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";

//module
import { CustomerListApi, CustomerListData, adjustLoyaltyResponse } from "../../components/customers/customerListModule";
import { CustomerDetailsProps, GetOneCustomerDetails, printMembershipCartPayload, printMembershipCartResponse } from "../../components/customers/addEditCustomerModule";
import { getAdminCustomerHistory as getAdminCustomerHistoryProps, getAdminCustomerHistoryData, getPosCustomerHistory as getPosCustomerHistoryProps, SendMailResponseType } from "../../components/customers/customerHistoryModule";
import { ZenToast } from "zen-core-elements";

//type
interface getAllCustomerListPayload { isActive: boolean, searchText: null | string, offset: number, limit: number, filterData: string, sort: string }
interface adjustLoyaltyPayload { "amount": number, "adjustmentType": string, "description": string | null }

interface commonType {
    loading: boolean,
    error: string | null,
}
interface initialState {
    isCustomerDetailsDirty: boolean
    isContactInfoDirty: boolean
    customerList: { active: { count: number, value: CustomerListData[], loading: boolean }, inActive: { count: number, value: CustomerListData[], loading: boolean } }
    oneCustomerDetails: GetOneCustomerDetails | null
    adjustLoyalty: commonType & { value: adjustLoyaltyResponse | null }
    adminCustomerHistory: commonType & { value: getAdminCustomerHistoryData[] | null }
    posCustomerHistory: commonType & { value: getPosCustomerHistoryProps | null }
}

const initialState: initialState = {
    isCustomerDetailsDirty: false,
    isContactInfoDirty: false,
    customerList: { active: { count: 0, loading: true, value: [] }, inActive: { count: 0, loading: true, value: [] } },
    oneCustomerDetails: null,
    adjustLoyalty: { loading: false, value: null, error: "" },
    adminCustomerHistory: { loading: false, value: null, error: "" },
    posCustomerHistory: { loading: true, value: null, error: "" }
}

export const getAllCustomerList = createAsyncThunk("customer/getAllCustomerList", ({ storeId, data }: { storeId: number, data: getAllCustomerListPayload }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPOSCustomer, { storeId: storeId })
    return HttpRoutingService?.getMethod<CustomerListApi>(url, data)
})
export const getOneCustomerDetails = createAsyncThunk("customer/getOneCustomerDetails", ({ storeId, customerId }: { storeId: number, customerId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPOSCustomer, { storeId: storeId })
    return HttpRoutingService?.getMethod<GetOneCustomerDetails>(url + '/' + customerId)
})
export const createCustomer = createAsyncThunk("customer/createCustomer", ({ storeId, data, isActive /* dont remove this unused variable , its used inside the customer page */ }: { storeId: number, data: CustomerDetailsProps, isActive: boolean }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPOSCustomer, { storeId: storeId })
    return HttpRoutingService?.postMethod<{ createCustomerDetails: string, id: number }>(url, data)
})
export const editCustomer = createAsyncThunk("customer/editCustomer", ({ storeId, customerId, isActive,/* dont remove this unused variable , its used inside the customer page */  data, }: { storeId: number, customerId: number, isActive: boolean, data: CustomerDetailsProps, }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPOSCustomer, { storeId: storeId })
    return HttpRoutingService?.putMethod<{ updateCustomer: { msg: true } }>(url + '/' + customerId, data)
})
export const deleteCustomer = createAsyncThunk("customer/deleteCustomer", ({ storeId, userId }: { storeId: number, userId: number | number[] }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPOSCustomer, { storeId: storeId })
    return typeof userId == "number" ?
        HttpRoutingService?.deleteMethod<{ data: true }>((url + '/' + userId), null) :
        HttpRoutingService?.putMethod<{ data: true }>((url + '/delete'), { customerIds: userId })
})
export const adjustLoyalty = createAsyncThunk("customer/adjustLoyalty", ({ storeId, customerId, payload }: { storeId: number, customerId: number, payload: adjustLoyaltyPayload }) => {
    let url = HttpRoutingService.replaceUrl(API?.updatePoints, { storeId: storeId, customerId: customerId })
    return HttpRoutingService?.putMethod<adjustLoyaltyResponse>(url, payload)
})

export const getAdminCustomerHistory = createAsyncThunk("customer/getAdminCustomerHistory", ({ storeId, customerId, data }: { storeId: number, customerId: number, data: { searchText: null | string, offset: number, limit: number, filterData: string | null } }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAdminCustomerHistory, { storeId: storeId, customerId: customerId })
    return HttpRoutingService?.getMethod<getAdminCustomerHistoryProps>(url, data, "v1/")
})
export const getPosCustomerHistory = createAsyncThunk("customer/getPosCustomerHistory", ({ customerId, storeId }: { storeId: number, customerId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getPosCustomerHistory, { storeId: storeId, customerId: customerId })
    return HttpRoutingService?.getMethod<{ customerDetails: getPosCustomerHistoryProps }>(url)
})
export const updateCustomerStatus = createAsyncThunk("customer/updateCustomerStatus", async ({ storeId, customerId, isActive }: { storeId: number, customerId: number, isActive: boolean }) => {
    const url = HttpRoutingService?.replaceUrl(API?.updateCustomerStatus, { storeId, customerId })
    return HttpRoutingService?.putMethod<{ data: true }>(url, { isActive })
})
export const sendCustomerEmail = createAsyncThunk("customer/sendCustomerEmail", async ({ customerId, storeId }: { customerId: number, storeId: number }, { rejectWithValue }) => {
    const url = HttpRoutingService?.replaceUrl(API?.sendCustomerEmail, { customerId })
    try {
        const response = await HttpRoutingService?.getMethod<SendMailResponseType>(url, null, "v1/")
        return response
    } catch (error: any) {
        return rejectWithValue(error.response?.data || error.message)
    }
})
export const printMembershipCartApi = createAsyncThunk("customer/printMembershipCartApi", ({ storeId, customerId, data }: { storeId: number, customerId: number, data: printMembershipCartPayload }) => {
    const url = HttpRoutingService?.replaceUrl(API?.printMembershipCard, { storeId: storeId, customerId })
    return HttpRoutingService?.postMethod<printMembershipCartResponse>(url, data, null, "v2/")
})

export const customerSlice = createSlice({
    name: "customer",
    initialState,
    reducers: {
        setCustomerDetailsDirty: (state, action: PayloadAction<boolean>) => {
            state.isCustomerDetailsDirty = action?.payload
        },
        setContactInfoDirty: (state, action: PayloadAction<boolean>) => {
            state.isContactInfoDirty = action?.payload
        },
        clearOneCustomerDetails: (state) => {
            state.oneCustomerDetails = null
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllCustomerList?.pending, (state, action) => {
                const { data: { isActive } } = action?.meta?.arg
                const key = isActive ? "active" : "inActive"
                state.customerList[key].loading = true
            })
            .addCase(getAllCustomerList?.fulfilled, (state, action) => {
                const { data: { isActive, offset } } = action?.meta?.arg
                const key = isActive ? "active" : "inActive"
                const { count, rows } = action?.payload?.customers
                state.customerList[key].loading = false
                if (offset > 0) {
                    if (rows?.length) {
                        state.customerList[key].value = [...state.customerList[key].value, ...rows]
                    }
                } else {
                    state.customerList[key].value = rows ?? []
                }
                state.customerList[key].count = count ?? 0
            })
            .addCase(getAllCustomerList?.rejected, (state, action) => {
                const { data: { isActive } } = action?.meta?.arg
                const key = isActive ? "active" : "inActive"
                state.customerList[key].loading = false
                ZenToast({
                    type: "error",
                    message: "Failed to get the customer list"
                })
            })

            .addCase(updateCustomerStatus?.pending, (state, action) => {
                const { isActive } = action?.meta?.arg
                const key = !isActive ? "active" : "inActive"
                state.customerList[key].loading = true
            })
            .addCase(updateCustomerStatus?.rejected, (state, action) => {
                const { isActive } = action?.meta?.arg
                const key = !isActive ? "active" : "inActive"
                state.customerList[key].loading = false
                ZenToast({
                    type: "error",
                    message: "Failed to update the customer status"
                })
            })

            .addCase(getOneCustomerDetails?.fulfilled, (state, action) => {
                state.oneCustomerDetails = action.payload
            })

            .addCase(adjustLoyalty?.pending, (state) => {
                state.adjustLoyalty.loading = true
            })
            .addCase(adjustLoyalty?.fulfilled, (state, action) => {
                state.adjustLoyalty.loading = false
                state.adjustLoyalty.value = action.payload
            })
            .addCase(adjustLoyalty?.rejected, (state, action) => {
                state.adjustLoyalty.loading = false
                state.adjustLoyalty.error = action?.error?.message as string
            })
            .addCase(getAdminCustomerHistory?.pending, (state) => {
                state.adminCustomerHistory.loading = true
            })
            .addCase(getAdminCustomerHistory?.fulfilled, (state, action) => {
                state.adminCustomerHistory.loading = false
                let { data } = action?.meta?.arg
                if (data?.offset > 0) {
                    if (action?.payload?.result?.userLogs?.rows?.length) {
                        state.adminCustomerHistory.value = [...state.adminCustomerHistory.value as [], ...action?.payload?.result?.userLogs?.rows]
                    }
                }
                else {
                    state.adminCustomerHistory.value = action?.payload?.result?.userLogs?.rows ? action?.payload?.result?.userLogs?.rows : []
                }
            })
            .addCase(getAdminCustomerHistory?.rejected, (state, action) => {
                state.adminCustomerHistory.loading = false
                state.adminCustomerHistory.error = action?.error?.message as string
            })
            .addCase(getPosCustomerHistory?.pending, (state) => {
                state.posCustomerHistory.loading = true
            })
            .addCase(getPosCustomerHistory?.fulfilled, (state, action) => {
                state.posCustomerHistory.loading = false
                state.posCustomerHistory.value = action.payload?.customerDetails
            })
            .addCase(getPosCustomerHistory?.rejected, (state, action) => {
                state.posCustomerHistory.loading = false
                state.posCustomerHistory.error = action?.error?.message as string
            })
            .addCase("NewSellscreenSlice/addCustomer", (state, action: any) => {
                const details = action.payload.customerDetails.details
                if (details) {
                    state.oneCustomerDetails = {
                        customerDetails: {
                            taxIdentificationNumber: null,
                            isWholesale: details.isWholesale ?? false,
                            websiteAddress: null,
                            storeName: null,
                            acceptsMarketing: true,
                            isApproved: details.isApproved ?? null,
                            taxExempt: false,
                            customerNotes: null,
                            customerCode: null,
                            accountLimit: details.onAccountDetails?.limit ? true : false,
                            isLoyalty: true,
                            accountLimitType: null,
                            accountLimitValue: details.onAccountDetails?.balance ?? null,
                            referredBy: null,
                            balancePoints: details.loyaltyPoints?.toString() ?? null,
                            earnedPoints: null,
                            redeemPoints: null,
                            user: {
                                firstName: details.firstName ?? "",
                                lastName: details.lastName ?? "",
                                email: details.email ?? "",
                                phone: details.phone ?? "",
                                countryCode: details.countryCode ?? 91,
                                isSmsVerified: details.isSmsVerified ?? false,
                                isActive: true,
                                gender: null,
                                dateOfBirth: details.dateOfBirth ?? null,
                                userId: details.id
                            },
                            roles: {
                                roleName: details.role?.role ?? "",
                                roleCode: details.role?.code ?? ""
                            },
                            addresses: [],
                            isAllowManualOrder: false
                        }
                    }
                } else {
                    state.oneCustomerDetails = null
                }
            })
            .addCase("NewSellscreenSlice/retriveSale", (state, action: any) => {
                const details = action.payload.customerDetails
                if (details) {
                    state.oneCustomerDetails = {
                        customerDetails: {
                            taxIdentificationNumber: null,
                            isWholesale: details.isWholesale ?? false,
                            websiteAddress: null,
                            storeName: null,
                            acceptsMarketing: true,
                            isApproved: details.isApproved ?? null,
                            taxExempt: false,
                            customerNotes: null,
                            customerCode: null,
                            accountLimit: details.onAccountDetails?.limit ? true : false,
                            isLoyalty: true,
                            accountLimitType: null,
                            accountLimitValue: details.onAccountDetails?.balance ?? null,
                            referredBy: null,
                            balancePoints: details.loyaltyPoints?.toString() ?? null,
                            earnedPoints: null,
                            redeemPoints: null,
                            user: {
                                firstName: details.firstName ?? "",
                                lastName: details.lastName ?? "",
                                email: details.email ?? "",
                                phone: details.phone ?? "",
                                countryCode: details.countryCode ?? 91,
                                isSmsVerified: details.isSmsVerified ?? false,
                                isActive: true,
                                gender: null,
                                dateOfBirth: details.dateOfBirth ?? null,
                                userId: details.id
                            },
                            roles: {
                                roleName: details.role?.role ?? "",
                                roleCode: details.role?.code ?? ""
                            },
                            addresses: [],
                            isAllowManualOrder: false
                        }
                    }
                } else {
                    state.oneCustomerDetails = null
                }
            })
            .addCase("NewSellscreenSlice/addParkSale", (state) => {
                state.oneCustomerDetails = null
            })
            .addCase("NewSellscreenSlice/resetSale", (state) => {
                state.oneCustomerDetails = null
            })
    }
})

export const { setCustomerDetailsDirty, setContactInfoDirty, clearOneCustomerDetails } = customerSlice?.actions
export default customerSlice?.reducer